import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';

import { priceAdjustedForDaysPerWeek } from 'helpers/price';

import Head from 'components/Head';

import { app, media } from 'context';
import { Button, Layout } from 'ds';
import { LISTING_PATH, OFFICE_REQUEST_PATH } from 'routes';
import { ListingWorkspace } from 'shared';
import { selectUser } from 'store/User/selectors';
import { useAppSelector } from 'store/hooks';
import MobileCarousel from 'ux/Public/Listing/MobileCarousel';

import About from './About';
import CtaBox from './CtaBox';
import Divider from './Divider';
import Hero from './Hero';
import Highlights from './Highlights';
import HowItWorks from './HowItWorks';
import Location from './Location';
import NearbyWorkspaces from './NearbyWorkspaces';
import OfficeSetupMadeEasy from './OfficeSetupMadeEasy';
import OfficeWillRunItself from './OfficeWillRunItself';
import PhotoCarousel from './PhotoCarousel';
import RealEstateListingSchema from './RealEstateListingSchema';
import ServicesModalStepper from './ServicesModalStepper';
import TourCTA from './TourCTA';
import { LISTING_MAX_CONTENT_WIDTH } from './constants';

interface Props {
  workspace: ListingWorkspace;
}

const ListingUI: React.FC<Props> = ({
  workspace,
  workspace: { short_name, address, name, monthly_price, nearby_workspaces, matterport_id, tour_video }
}) => {
  const [selectedDaysRate, setSelectedDaysRate] = useState<number>();
  const { navBarHeight, setOverflowX } = useContext(app);
  const history = useHistory();
  const location = useLocation();
  const { days_per_week: qsDaysPerWeek } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const daysPerWeek = typeof qsDaysPerWeek === 'string' && qsDaysPerWeek.length ? parseInt(qsDaysPerWeek) : 5;
  const { xs, sm, isMobile } = useContext(media);
  const { contentWidth } = useContext(app);
  const photos = [...workspace.images];
  const floorPlans = [...workspace.floor_plans];
  const maxContentWidth = Math.min(contentWidth, LISTING_MAX_CONTENT_WIDTH);
  const tourVideoKey = tour_video?.key || null;
  const user = useAppSelector(selectUser);
  const isProspectVersion = location.pathname.includes(LISTING_PATH) && !!user;
  const [isServicesModalVisible, setIsServicesModalVisible] = useState(false);
  const ctaBoxRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (!setOverflowX) return;

    if (xs) {
      setOverflowX('hidden');
    } else {
      setOverflowX('visible');
    }

    return () => {
      setOverflowX && setOverflowX('hidden');
    };
  }, [!!setOverflowX, xs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!monthly_price) return;

    setSelectedDaysRate(
      priceAdjustedForDaysPerWeek({ monthlyPrice: monthly_price, daysPerWeek: Math.min(5, daysPerWeek) })
    );
  }, [monthly_price, daysPerWeek]);

  const setDaysPerWeek = (daysPerWeek: number) => {
    history.replace({ search: generateSearch({ search: location.search, daysPerWeek }) });
  };
  const ctaBoxMarginLeft = xs || sm ? 32 : 40;
  const HeroCTA = <TourCTA workspace={workspace} textColor="white" showAvailabilityStartDate={false} />;

  return (
    <>
      <Head
        title={`${address.city} Office Space${!!address.neighborhood ? ` in ${address.neighborhood}` : ''} | ${name}`}
        description={workspace.description}
      />
      <RealEstateListingSchema workspace={workspace} />
      {!xs && !!HeroCTA && (
        <Hero workspace={workspace} CTA={HeroCTA} isProspectVersion={isProspectVersion} daysPerWeek={daysPerWeek} />
      )}
      {!xs && !HeroCTA && (
        <Hero
          workspace={workspace}
          handleRequestTour={() => {
            const ctaBox = ctaBoxRef.current;

            if (!ctaBox) return;

            window.scrollTo({ top: ctaBox.offsetTop - 120, behavior: 'smooth' });
          }}
          isProspectVersion={isProspectVersion}
          daysPerWeek={daysPerWeek}
        />
      )}
      {xs && (
        <MobileCarousel
          backLabel={isProspectVersion ? short_name || '' : name}
          images={photos}
          floorPlans={floorPlans}
          matterportId={matterport_id}
          tourVideoKey={tourVideoKey}
          workspace={workspace}
        />
      )}
      {xs && (
        <CtaBox
          workspace={workspace}
          selectedDaysRate={selectedDaysRate}
          daysPerWeek={daysPerWeek}
          setDaysPerWeek={setDaysPerWeek}
        />
      )}
      <Layout width="100%" justify={xs ? undefined : 'center'}>
        <Layout marginTop={xs ? 60 : 32} __style={{ maxWidth: maxContentWidth }} direction="column">
          <Highlights workspace={workspace} />
          {!xs && (
            <>
              <Layout paddingTop={photos.length > 1 ? 44 : undefined} width="100%" justify="center">
                {photos.length > 1 && (
                  <Layout width={Math.min(contentWidth, 1080)}>
                    <PhotoCarousel
                      images={photos}
                      floorPlans={floorPlans}
                      matterportId={matterport_id}
                      tourVideoKey={tourVideoKey}
                      workspace={workspace}
                    ></PhotoCarousel>
                  </Layout>
                )}
              </Layout>
              <Divider />
            </>
          )}
          <Layout direction="row" justify="space-between">
            <Layout marginTop={xs ? 36 : undefined}>
              <About workspace={workspace} />
              <Divider />
              <OfficeSetupMadeEasy
                onCardClick={index => {
                  setCurrentStep(index);
                  setIsServicesModalVisible(true);
                }}
              />
              <Divider />
              <OfficeWillRunItself />
              <Layout marginY={!isMobile ? 36 : undefined} borderBottom borderColor="gray-50" marginBottom={36} />
              <HowItWorks />
              <Divider />
              <Location workspace={workspace} />
            </Layout>
            {!xs && (
              <Layout
                marginLeft={ctaBoxMarginLeft}
                __ref={ctaBoxRef}
                position="sticky"
                __style={{ alignSelf: 'flex-start' }}
                top={navBarHeight + 36}
              >
                <CtaBox
                  workspace={workspace}
                  selectedDaysRate={selectedDaysRate}
                  daysPerWeek={daysPerWeek}
                  setDaysPerWeek={setDaysPerWeek}
                />
              </Layout>
            )}
          </Layout>
          {!!nearby_workspaces && !isProspectVersion && (
            <Layout direction="column" marginTop={isMobile ? 64 : 44} paddingTop={36}>
              <NearbyWorkspaces workspaces={nearby_workspaces} />
              <Layout width="100%" marginTop={isMobile ? 36 : 40} justify="center">
                <Button
                  text={isMobile ? 'Find an office' : 'Request an office'}
                  size="md"
                  type="primary"
                  textSize="body-sm"
                  href={OFFICE_REQUEST_PATH}
                />
              </Layout>
            </Layout>
          )}
        </Layout>
      </Layout>
      <ServicesModalStepper
        isVisible={isServicesModalVisible}
        onClose={() => setIsServicesModalVisible(false)}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
    </>
  );
};

const generateSearch = ({ daysPerWeek, search }: { daysPerWeek: number; search: string }) => {
  const searchParams = new URLSearchParams(search);

  searchParams.set('days_per_week', daysPerWeek.toString());

  return searchParams.toString();
};

export default ListingUI;
